import { useEffect, useRef } from "react";
import { ReactComponent as TasksActive } from "../../Assets/check-done-01-active.svg";
import { ReactComponent as Tasks } from "../../Assets/check-done-01.svg";
import { ReactComponent as HelpCenterActive } from "../../Assets/circle-question-solid.svg";
import { ReactComponent as HelpCenter } from "../../Assets/circle-question.svg";
import { ReactComponent as Home } from "../../Assets/house-blank-regular.svg";
import { ReactComponent as HomeActive } from "../../Assets/house-blank-solid.svg";
import { ReactComponent as Roadmap } from "../../Assets/map-location-regular.svg";
import { ReactComponent as RoadmapActive } from "../../Assets/map-location-solid.svg";
import { ReactComponent as Announcements } from "../../Assets/megaphone-regular.svg";
import { ReactComponent as AnnouncementsActive } from "../../Assets/megaphone-solid.svg";
import { ReactComponent as Chat } from "../../Assets/message-regular.svg";
import { ReactComponent as ChatActive } from "../../Assets/message-solid.svg";
import Communicator from "../../Helper/Communicator";
import { useChecklistStore } from "../../Stores/Checklist";
import { useConfigStore } from "../../Stores/Config";
import { useRouterStore } from "../../Stores/Router";
import { useTicketStore } from "../../Stores/Ticket";
import { homeVersionClass } from "../../Views/Home/homeVersions";
import { hideTabBarForNavigation } from "./tabBarVisibility";
import "./TabBar.scss";

export const usePrevious = (value: any) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });

  return ref.current as any;
};

function TabBar() {
  const routerStore = useRouterStore();
  const configStore = useConfigStore();
  const ticketStore = useTicketStore();
  const checklistStore = useChecklistStore();
  const currentPage = routerStore.currentPage;
  const unreadTickets = ticketStore.unreadTickets;

  const renderChecklistsTab = () => {
    if (
      configStore.config.hideChecklists ||
      checklistStore.checklists?.length === 0
    ) {
      return null;
    }

    const active =
      currentPage === "checklists" || currentPage === "checklistdetails";

    return (
      <button
        type="button"
        className={`tab-bar-item ${active ? "active" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={() => routerStore.setPage("checklists")}
      >
        <div className="icon-container">
          {active ? (
            <TasksActive aria-hidden="true" focusable="false" />
          ) : (
            <Tasks aria-hidden="true" focusable="false" />
          )}
          <div className="tab-bar-item--unread" aria-hidden="true">
            {checklistStore.checklists?.length}
          </div>
        </div>
        <span>{configStore.config.checklistMenuLabel}</span>
      </button>
    );
  };

  const renderNewsTab = () => {
    if (configStore.config.hideNews) {
      return null;
    }

    if (!configStore.appPermissionsLoaded) {
      return null;
    }

    if (configStore.deniedAppPermissions.includes("news")) {
      return null;
    }

    const active = currentPage === "news" || currentPage === "newsdetails";

    return (
      <button
        type="button"
        className={`tab-bar-item ${active ? "active" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={() => routerStore.setPage("news")}
      >
        <div className="icon-container">
          {active ? (
            <AnnouncementsActive aria-hidden="true" focusable="false" />
          ) : (
            <Announcements aria-hidden="true" focusable="false" />
          )}
        </div>
        <span>{configStore.config.newsMenuLabel}</span>
      </button>
    );
  };

  const renderRoadmapTab = () => {
    if (configStore.config.hideFeatureRequests) {
      return null;
    }

    if (!configStore.appPermissionsLoaded) {
      return null;
    }

    if (configStore.deniedAppPermissions.includes("featureRequests")) {
      return null;
    }

    const active = currentPage === "roadmap";

    return (
      <button
        type="button"
        className={`tab-bar-item ${active ? "active" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={() => {
          routerStore.setPage("roadmap");
        }}
      >
        <div className="icon-container">
          {active ? (
            <RoadmapActive aria-hidden="true" focusable="false" />
          ) : (
            <Roadmap aria-hidden="true" focusable="false" />
          )}
        </div>
        <span>{configStore.config.featureRequestsMenuLabel}</span>
      </button>
    );
  };

  const renderConversations = () => {
    if (configStore.config.hideAllConversations) {
      return null;
    }

    const active =
      currentPage === "conversations" || currentPage === "conversation";

    return (
      <button
        type="button"
        className={`tab-bar-item ${active ? "active" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={() => routerStore.setPage("conversations")}
      >
        <div className="icon-container">
          {active ? (
            <ChatActive aria-hidden="true" focusable="false" />
          ) : (
            <Chat aria-hidden="true" focusable="false" />
          )}
          {unreadTickets && unreadTickets.length > 0 && (
            <div className="tab-bar-item--unread" aria-hidden="true">
              {unreadTickets.length > 9 ? "9+" : unreadTickets.length}
            </div>
          )}
        </div>
        <span>{configStore.config.allConversationsMenuLabel}</span>
      </button>
    );
  };

  const renderHelpCenter = () => {
    if (configStore.config.hideKnowledgeBase) {
      return null;
    }

    if (!configStore.appPermissionsLoaded) {
      return null;
    }

    if (configStore.deniedAppPermissions.includes("knowledgeBase")) {
      return null;
    }

    const externalUrl = configStore.config.knowledgeBaseExternalUrl;
    const active = !externalUrl && currentPage.startsWith("helpcenter");

    return (
      <button
        type="button"
        className={`tab-bar-item ${active ? "active" : ""}`}
        aria-current={active ? "page" : undefined}
        onClick={() => {
          if (externalUrl) {
            Communicator.openURL(externalUrl, true);
            return;
          }
          routerStore.setPage("helpcenter");
        }}
      >
        <div className="icon-container">
          {active ? (
            <HelpCenterActive aria-hidden="true" focusable="false" />
          ) : (
            <HelpCenter aria-hidden="true" focusable="false" />
          )}
        </div>
        <span>{configStore.config.knowledgeBaseMenuLabel}</span>
      </button>
    );
  };

  if (
    configStore.config.hideNews &&
    configStore.config.hideFeatureRequests &&
    configStore.config.hideAllConversations &&
    configStore.config.hideKnowledgeBase
  ) {
    try {
      const homePage: HTMLElement | null =
        document?.body.querySelector(".home-page");
      if (homePage) {
        homePage.style.height = "100vh";
      }
    } catch (_) {}
    return <></>;
  }

  if (
    hideTabBarForNavigation({
      showBackButton: routerStore.showBackButton,
      historyLength: routerStore.history.length,
    })
  ) {
    return null;
  }

  const conversations = renderConversations();
  const news = renderNewsTab();
  const roadmap = renderRoadmapTab();
  const checklists = renderChecklistsTab();
  const helpCenter = renderHelpCenter();

  const activeTabsCount = [
    conversations,
    news,
    roadmap,
    checklists,
    helpCenter,
  ].filter((tab) => tab !== null).length;
  if (activeTabsCount === 0) {
    return null;
  }

  return (
    <>
      <nav
        className={`tab-bar ${activeTabsCount <= 1 && "tab-bar--simple"} ${homeVersionClass(
          configStore.getHomeVersion(),
        )}`}
        aria-label={configStore.translateTextWithFallback(
          "navigation",
          "Navigation",
        )}
      >
        <button
          type="button"
          className={`tab-bar-item ${currentPage === "menu" ? "active" : ""}`}
          aria-current={currentPage === "menu" ? "page" : undefined}
          onClick={() => routerStore.setPage("menu")}
        >
          <div className="icon-container">
            {currentPage === "menu" ? (
              <HomeActive aria-hidden="true" focusable="false" />
            ) : (
              <Home aria-hidden="true" focusable="false" />
            )}
          </div>
          <span>{configStore.translateText("homeLabel")}</span>
        </button>
        {conversations}
        {news}
        {roadmap}
        {checklists}
        {helpCenter}
      </nav>
    </>
  );
}

export default TabBar;
